import {parseDate} from 'common/date';
import * as React from 'react';
import CalendarSelect from 'src/DatePicker/CalendarSelect';
import {TextInput} from 'src/inputs/TextInput';
import {Popover} from 'src/Popover/NewPopover';
import type {DateTime} from 'luxon';

type Props = {
  selectedDate: DateTime;
  onChange: (newDate: DateTime) => void;
  /** The input's id, for a label. */
  id?: string;
  /** Marks the date as one that won't do. */
  invalid?: boolean;
};

const DatePicker = (props: Props) => {
  const {selectedDate, onChange} = props;

  const [draft, setDraft] = React.useState<string>(selectedDate.toISODate());
  const [open, setOpen] = React.useState<boolean>(false);

  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const calendarRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    setDraft(selectedDate.toISODate());
  }, [selectedDate]);

  const handleInputFocus = () => {
    inputRef.current?.setSelectionRange(0, draft.length);
    setOpen(true);
  };
  const handleInputChange = (newDraft: string) => {
    setOpen(true);
    setDraft(newDraft);
  };
  // Push a typed draft up to the parent, reverting the field when it doesn't
  // parse. Shared by blur, Enter, and dismissing the calendar.
  const commitDraft = () => {
    const maybeDate = parseDate(draft);
    if (maybeDate.isValid) {
      onChange(maybeDate);
    } else {
      setDraft(selectedDate.toISODate());
    }
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    commitDraft();

    if (
      !calendarRef.current ||
      !(e.relatedTarget instanceof Node) ||
      !calendarRef.current.contains(e.relatedTarget)
    ) {
      setOpen(false);
    }
  };
  const handleCancel = () => {
    commitDraft();
    setOpen(false);
  };
  const selectDate = (date: DateTime) => {
    onChange(date);
  };

  const handleSelectDate = (newDate: DateTime) => {
    selectDate(newDate);
    inputRef.current?.focus();
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // A typed draft otherwise reaches the parent only on blur, so an Enter
    // that implicitly submits the surrounding form would submit the previous
    // date and silently drop the visible edit. Swallow that first Enter to
    // commit (or revert) the draft; once the field and the committed date
    // agree, Enter falls through and submits as usual.
    if (e.key === 'Enter') {
      if (draft !== selectedDate.toISODate()) {
        e.preventDefault();
        commitDraft();
        setOpen(false);
      }
      return;
    }

    const maybeDate = parseDate(draft);
    if (maybeDate.isValid) {
      if (e.key === 'ArrowLeft') {
        selectDate(maybeDate.minus({days: 1}));
      } else if (e.key === 'ArrowRight') {
        selectDate(maybeDate.plus({days: 1}));
      } else if (e.key === 'ArrowUp') {
        selectDate(maybeDate.minus({days: 7}));
      } else if (e.key === 'ArrowDown') {
        selectDate(maybeDate.plus({days: 7}));
      }
    }
  };

  return (
    <>
      <TextInput
        id={props.id}
        aria-invalid={props.invalid}
        ref={inputRef}
        selectOnFocus
        onFocus={handleInputFocus}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleInputBlur}
        value={draft}
      />
      <Popover
        anchorRef={inputRef}
        anchor="bottom-left"
        open={open}
        onOutsideClick={handleCancel}
        onOutsideFocus={handleCancel}
      >
        <CalendarSelect
          ref={calendarRef}
          selectedDate={selectedDate}
          onDateSelect={handleSelectDate}
        />
      </Popover>
    </>
  );
};

export default DatePicker;
